import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOverlayState } from '../context/OverlayContext';
import { updateEvent } from '../services/eventService';
import { Event } from '../types';
import { Tag, Users, ChevronLeft, ChevronRight, Check, Info, X, Shield } from 'lucide-react';
import LocationSearch from './LocationSearch';
import InterestSelector from './InterestSelector';
import DatePicker from './DatePicker';
import { motion, AnimatePresence } from 'framer-motion';
import { AVAILABLE_INTERESTS } from '../constants/interests';
import { containsProfanity } from '../utils/contentFilter';

interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventUpdated: () => void;
  event: Event;
}

type GenderPref = 'male' | 'female' | 'prefer-not-to-say' | 'non-binary';
type Status =
  | 'draft'
  | 'published'
  | 'expired'
  | 'cancelled'
  | 'archived';

const EditEventModal: React.FC<EditEventModalProps> = ({ isOpen, onClose, onEventUpdated, event }) => {
  const { currentUser } = useAuth();
  useOverlayState(isOpen, 'edit-event-modal');
  const [step, setStep] = useState(1);

  const isOwner = currentUser?.id === event?.posterId;
  const isAdmin = currentUser?.isAdmin;
  const isAdminEdit = isAdmin && !isOwner;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    date: '',
    category: '',
    maxApplicants: '',
    preferredGender: 'prefer-not-to-say' as GenderPref,
    minAge: '',
    maxAge: '',
    preferredInterests: [] as string[],
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    status: 'published' as Status,
    imageUrl: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Initialize form data from event prop
  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title,
        description: event.description,
        location: event.location,
        date: event.date,
        category: event.category,
        maxApplicants: event.maxApplicants?.toString() || '10',
        preferredGender: (event.preferredDemographics?.gender as GenderPref) || 'prefer-not-to-say',
        minAge: event.preferredDemographics?.minAge?.toString() || '',
        maxAge: event.preferredDemographics?.maxAge?.toString() || '',
        preferredInterests: event.preferredDemographics?.interests || [],
        latitude: event.latitude,
        longitude: event.longitude,
        status: (event.status as Status) || 'published',
        imageUrl: event.imageUrl || '',
      });
    }
  }, [event, isOpen]);

  const categories = [...AVAILABLE_INTERESTS];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      const { [name]: _omit, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleInterestChange = (interests: string[]) => {
    setFormData(prev => ({ ...prev, preferredInterests: interests }));
    if (errors.preferredInterests) {
      const { preferredInterests: _omit, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleLocationSelect = (location: { name: string; latitude: number; longitude: number }) => {
    setFormData(prev => ({
      ...prev,
      location: location.name,
      latitude: location.latitude,
      longitude: location.longitude,
    }));
    if (errors.location) {
      const { location: _omit, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleDateChange = (date: string) => {
    setFormData(prev => ({ ...prev, date }));
    if (errors.date) {
      const { date: _omit, ...rest } = errors;
      setErrors(rest);
    }
  };

  const validateStep = (currentStep: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (currentStep) {
      case 1:
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';

        // Check for inappropriate content
        if (containsProfanity(formData.title)) {
          newErrors.title = 'Please remove inappropriate language from the title.';
        }
        if (containsProfanity(formData.description)) {
          newErrors.description = 'Please remove inappropriate language from the description.';
        }
        break;
      case 2:
        if (!formData.location.trim()) newErrors.location = 'Location is required';
        if (!formData.date || formData.date.includes('00')) newErrors.date = 'Date is required';
        if (!formData.category) newErrors.category = 'Category is required';
        break;
      case 3:
        if (
          formData.maxApplicants &&
          (isNaN(Number(formData.maxApplicants)) || Number(formData.maxApplicants) <= 0)
        ) {
          newErrors.maxApplicants = 'Maximum applicants must be a positive number';
        }
        if (formData.maxApplicants && Number(formData.maxApplicants) > 10) {
          newErrors.maxApplicants = 'Maximum applicants cannot exceed 10';
        }
        if (formData.minAge && (isNaN(Number(formData.minAge)) || Number(formData.minAge) < 18)) {
          newErrors.minAge = 'Minimum age must be at least 18';
        }
        if (
          formData.maxAge &&
          (isNaN(Number(formData.maxAge)) || Number(formData.maxAge) < Number(formData.minAge || 18))
        ) {
          newErrors.maxAge = 'Maximum age must be greater than minimum age';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const handleCancel = () => setShowCancelConfirm(true);
  const handleConfirmCancel = () => onClose();

  const buildUpdatePayload = (statusOverride?: Status) => ({
    title: formData.title,
    description: formData.description,
    location: formData.location,
    date: formData.date,
    category: formData.category,
    latitude: formData.latitude,
    longitude: formData.longitude,
    maxApplicants: Number(formData.maxApplicants),
    preferredDemographics: {
      gender: formData.preferredGender,
      minAge: formData.minAge ? Number(formData.minAge) : undefined,
      maxAge: formData.maxAge ? Number(formData.maxAge) : undefined,
      interests: formData.preferredInterests,
    },
    ...(statusOverride ? { status: statusOverride } : {}),
    ...(isAdmin && formData.imageUrl ? { imageUrl: formData.imageUrl } : {}),
  });

  const handleSaveAsDraft = async () => {
    if (!currentUser || !event) return;

    setIsSubmitting(true);
    setErrors({});
    try {
      const updatedEvent = await updateEvent(event.id, buildUpdatePayload('draft'));
      if (updatedEvent) {
        setSuccess(true);
        setTimeout(() => {
          onEventUpdated();
          onClose();
        }, 2000);
      } else {
        setErrors({ submit: 'Failed to save draft. Please try again.' });
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to save draft. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!validateStep(step) || !currentUser || !event) return;

    setIsSubmitting(true);
    setErrors({});
    try {
      const updatedEvent = await updateEvent(event.id, buildUpdatePayload('published'));
      if (updatedEvent) {
        setSuccess(true);
        setTimeout(() => {
          onEventUpdated();
          onClose();
        }, 2000);
      } else {
        setErrors({ submit: 'Failed to publish event. Please try again.' });
      }
    } catch (error) {
      console.error('Failed to publish event:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to publish event. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(step) || !currentUser || !event) return;

    setIsSubmitting(true);
    setErrors({});
    try {
      const updatedEvent = await updateEvent(event.id, buildUpdatePayload());
      if (updatedEvent) {
        setSuccess(true);
        setTimeout(() => {
          onEventUpdated();
          onClose();
        }, 2000);
      } else {
        setErrors({ submit: 'Failed to update event. Please try again.' });
      }
    } catch (error) {
      console.error('Failed to update event:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to update event. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (showCancelConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-primary-50 rounded-lg shadow-elegant p-6 max-w-md w-full border border-primary-200"
        >
          <h3 className="text-xl font-semibold mb-4 text-contrast">Cancel Editing?</h3>
          <p className="text-contrast-400 mb-6">Are you sure you want to cancel? All your changes will be lost.</p>
          <div className="flex justify-end space-x-4">
            <button onClick={() => setShowCancelConfirm(false)} className="px-4 py-2 text-contrast hover:text-contrast-600">
              Continue Editing
            </button>
            <button onClick={handleConfirmCancel} className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-4 py-2.5 rounded-full transition-colors font-semibold shadow-lg">
              Yes, Cancel
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-theme-cards rounded-2xl shadow-card border border-theme-cards w-full max-w-4xl my-8">
        <div className="p-4 border-b border-primary-200 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-contrast">Edit Vybe</h2>
            {isAdminEdit && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent2/20 border border-accent2/40">
                <Shield size={14} className="text-accent2" />
                <span className="text-xs font-medium text-accent2">Admin</span>
              </div>
            )}
          </div>
          <button onClick={handleCancel} className="text-contrast-500 hover:text-contrast p-1 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {success ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8">
              <div className="w-16 h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={32} className="text-accent2" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-contrast">Vybe Updated Successfully!</h2>
              <p className="text-contrast-400 mb-4">
                Your Vybe has been updated successfully. You can manage your Vybes and view requests from the "My Vybes"
                page.
              </p>
            </motion.div>
          ) : (
            <>
              {/* Progress Steps */}
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  {[1, 2, 3].map(stepNumber => (
                    <div key={stepNumber} className="flex items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          stepNumber === step
                            ? 'bg-secondary text-white'
                            : stepNumber < step
                            ? 'bg-accent2 text-primary'
                            : 'bg-primary-200 text-contrast-600'
                        }`}
                      >
                        {stepNumber < step ? <Check size={20} /> : stepNumber}
                      </div>
                      <div className={`ml-2 ${stepNumber === step ? 'font-medium text-contrast' : 'text-contrast-500'}`}>
                        {stepNumber === 1 ? 'Basic Info' : stepNumber === 2 ? 'Event Details' : 'Preferences'}
                      </div>
                      {stepNumber < 3 && <div className="w-12 md:w-24 border-t border-primary-300 mx-2"></div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Content */}
              {errors.submit && (
                <div className="bg-secondary/10 text-secondary p-4 rounded-md mb-6 flex items-start">
                  <Info size={20} className="mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Error</p>
                    <p>{errors.submit}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <AnimatePresence mode="wait">
                  {/* Step 1: Basic Info */}
                  {step === 1 && (
                    <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="text-xl font-bold mb-6 text-contrast">Basic Information</h2>

                      <div className="mb-6">
                        <label htmlFor="title" className="block text-sm font-medium text-contrast mb-1">
                          Vybe Title*
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary ${
                            errors.title ? 'border-secondary' : 'border-primary-200'
                          }`}
                          placeholder="Give your Vybe a clear, descriptive title"
                        />
                        {errors.title && <p className="text-secondary text-sm mt-1">{errors.title}</p>}
                      </div>

                      <div className="mb-6">
                        <label htmlFor="description" className="block text-sm font-medium text-contrast mb-1">
                          Description*
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          rows={6}
                          className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary ${
                            errors.description ? 'border-secondary' : 'border-primary-200'
                          }`}
                          placeholder="Describe your Vybe in detail. What will participants do? What should they expect? Why should they join?"
                        />
                        {errors.description && <p className="text-secondary text-sm mt-1">{errors.description}</p>}
                        <p className="text-sm text-contrast-500 mt-1">Be specific and include any important details participants should know.</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Event Details */}
                  {step === 2 && (
                    <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="text-xl font-bold mb-6 text-contrast">Vybe Details</h2>

                      <div className="mb-6">
                        <label htmlFor="location" className="block text-sm font-medium text-contrast mb-1">
                          Location*
                        </label>
                        <LocationSearch
                          onLocationSelect={handleLocationSelect}
                          placeholder="Enter Vybe location"
                          initialLocation={formData.location}
                        />
                        {errors.location && <p className="text-secondary text-sm mt-1">{errors.location}</p>}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label htmlFor="date" className="block text-sm font-medium text-contrast mb-1">
                            Date*
                          </label>
                          <DatePicker
                            selectedDate={formData.date}
                            onChange={handleDateChange}
                            minDate={new Date().toISOString().split('T')[0]}
                            mode="future"
                          />
                          {errors.date && <p className="text-secondary text-sm mt-1">{errors.date}</p>}
                        </div>

                        <div>
                          <label htmlFor="category" className="block text-sm font-medium text-contrast mb-1">
                            Category*
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Tag size={18} className="text-contrast-500" />
                            </div>
                            <select
                              id="category"
                              name="category"
                              value={formData.category}
                              onChange={handleChange}
                              className={`w-full pl-10 p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary appearance-none ${
                                errors.category ? 'border-secondary' : 'border-primary-200'
                              }`}
                            >
                              <option value="">Select a category</option>
                              {categories.map(category => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </select>
                          </div>
                          {errors.category && <p className="text-secondary text-sm mt-1">{errors.category}</p>}
                        </div>
                      </div>

                      <div className="mb-6">
                        <label htmlFor="maxApplicants" className="block text-sm font-medium text-contrast mb-1">
                          Maximum Tribe Members
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Users size={18} className="text-contrast-500" />
                          </div>
                          <input
                            type="number"
                            id="maxApplicants"
                            name="maxApplicants"
                            value={formData.maxApplicants}
                            onChange={handleChange}
                            min="1"
                            max="10"
                            required
                            className={`w-full pl-10 p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary ${
                              errors.maxApplicants ? 'border-secondary' : 'border-primary-200'
                            }`}
                            placeholder="Enter number (max 10)"
                          />
                        </div>
                        {errors.maxApplicants && <p className="text-secondary text-sm mt-1">{errors.maxApplicants}</p>}
                        <p className="text-sm text-contrast-500 mt-1">Maximum 10 participants.</p>
                      </div>

                      {/* Admin Image URL (only for admin users) */}
                      {isAdmin && (
                        <div className="mb-6 p-4 bg-accent2/5 border border-accent2/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <Shield size={16} className="text-accent2" />
                            <h3 className="text-sm font-semibold text-contrast">Admin: Change Vybe Image</h3>
                          </div>
                          <label htmlFor="imageUrl" className="block text-sm font-medium text-contrast mb-1">
                            Unsplash Direct Image URL
                          </label>
                          <input
                            type="text"
                            id="imageUrl"
                            name="imageUrl"
                            value={formData.imageUrl}
                            onChange={handleChange}
                            className="w-full p-3 bg-primary-100 border border-blue-400/30 hover:border-blue-400/50 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all text-contrast text-sm"
                            placeholder="https://images.unsplash.com/photo-..."
                          />
                          {formData.imageUrl && formData.imageUrl.includes('images.unsplash.com') && (
                            <div className="text-sm text-green-600 flex items-center gap-2 mt-2">
                              <Check size={16} />
                              <span>Valid image URL - will be updated on save</span>
                            </div>
                          )}
                          {formData.imageUrl && !formData.imageUrl.includes('images.unsplash.com') && formData.imageUrl.trim() !== '' && (
                            <div className="text-sm text-amber-600 flex items-center gap-2 mt-2 p-2 bg-amber-50 rounded">
                              <Info size={16} />
                              <span>Make sure to use the direct image URL (should start with images.unsplash.com)</span>
                            </div>
                          )}
                          <div className="text-xs text-contrast-500 mt-2 space-y-2">
                            <p className="font-medium">How to get the correct URL:</p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                              <li>Open the Unsplash photo page</li>
                              <li>Right-click on the image and select "Copy Image Address"</li>
                              <li>Paste the URL here (should start with <code className="bg-primary-200 px-1 rounded">images.unsplash.com</code>)</li>
                            </ol>
                            <p className="pt-1">Leave blank to keep the current image.</p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Step 3: Preferences */}
                  {step === 3 && (
                    <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="text-xl font-bold mb-6 text-contrast">Preferences</h2>

                      <div className="mb-6">
                        <h3 className="text-lg font-medium mb-3 text-contrast">Tribe Member Preferences</h3>
                        <p className="text-contrast-400 mb-4">
                          These preferences help match your Vybe with suitable Tribe Members. They are not strict requirements.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                          <div>
                            <label htmlFor="preferredGender" className="block text-sm font-medium text-contrast mb-1">
                              Gender Preference
                            </label>
                            <select
                              id="preferredGender"
                              name="preferredGender"
                              value={formData.preferredGender}
                              onChange={handleChange}
                              className="w-full p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                            >
                              <option value="prefer-not-to-say">Any Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="non-binary">Non-binary/Other</option>
                            </select>
                          </div>

                          <div>
                            <label htmlFor="minAge" className="block text-sm font-medium text-contrast mb-1">
                              Minimum Age
                            </label>
                            <input
                              type="number"
                              id="minAge"
                              name="minAge"
                              value={formData.minAge}
                              onChange={handleChange}
                              min="18"
                              className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary ${
                                errors.minAge ? 'border-secondary' : 'border-primary-200'
                              }`}
                              placeholder="e.g. 18"
                            />
                            {errors.minAge && <p className="text-secondary text-sm mt-1">{errors.minAge}</p>}
                          </div>

                          <div>
                            <label htmlFor="maxAge" className="block text-sm font-medium text-contrast mb-1">
                              Maximum Age
                            </label>
                            <input
                              type="number"
                              id="maxAge"
                              name="maxAge"
                              value={formData.maxAge}
                              onChange={handleChange}
                              min={formData.minAge || '18'}
                              className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary ${
                                errors.maxAge ? 'border-secondary' : 'border-primary-200'
                              }`}
                              placeholder="e.g. 65"
                            />
                            {errors.maxAge && <p className="text-secondary text-sm mt-1">{errors.maxAge}</p>}
                          </div>
                        </div>

                        <div className="mb-6">
                          <label className="block text-sm font-medium text-contrast mb-2">Preferred Interests</label>
                          <InterestSelector selectedInterests={formData.preferredInterests} onChange={handleInterestChange} />
                          <p className="text-sm text-contrast-500 mt-2">
                            Select interests that are relevant to your Vybe. This helps match your Vybe with interested Tribe Members.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 border-t border-primary-200 pt-4">
                  {step > 1 ? (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex items-center px-4 py-2 border border-primary-200 rounded-md hover:bg-primary-100 text-contrast transition-colors"
                    >
                      <ChevronLeft size={18} className="mr-2" />
                      Back
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="flex items-center px-4 py-2 border border-primary-200 rounded-md hover:bg-primary-100 text-contrast transition-colors"
                    >
                      <X size={18} className="mr-2" />
                      Cancel
                    </button>
                  )}

                  <div className="flex gap-3">
                    {step === 3 && formData.status === 'draft' && (
                      <button
                        type="button"
                        onClick={handleSaveAsDraft}
                        disabled={isSubmitting}
                        className="flex items-center px-4 py-2 bg-primary-100 text-contrast rounded-md hover:bg-primary-200 transition-colors disabled:bg-primary-200 disabled:text-contrast-500 disabled:cursor-not-allowed"
                      >
                        Save as Draft
                      </button>
                    )}

                    {step < 3 ? (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex items-center bg-secondary text-white px-4 py-2 rounded-md hover:bg-secondary-600 transition-colors"
                      >
                        Next
                        <ChevronRight size={18} className="ml-2" />
                      </button>
                    ) : (
                      <>
                        {formData.status === 'draft' ? (
                          <button
                            type="button"
                            onClick={handlePublish}
                            disabled={isSubmitting}
                            className="flex items-center bg-accent2 text-primary px-4 py-2 rounded-md hover:bg-accent2-600 transition-colors disabled:bg-neutral-400 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? 'Publishing...' : 'Publish Vybe'}
                            <Check size={18} className="ml-2" />
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center bg-accent2 text-primary px-4 py-2 rounded-md hover:bg-accent2-600 transition-colors disabled:bg-neutral-400 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? 'Updating...' : 'Update Vybe'}
                            <Check size={18} className="ml-2" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditEventModal;
